import { getCrop, deleteCrop, updateCrop, addCrop } from '@/adapters/master';
import { IDataModal } from '@/interfaces/data-modal';
import { ICrop, ICropRes } from '@/interfaces/master';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Badge,
  Flex,
  Form,
  FormProps,
  Modal,
  notification,
  Radio,
  Select,
  Table,
  TableColumnsType,
  Button,
} from 'antd';
import { useEffect, useMemo, useState, useCallback, Suspense } from 'react';
import { uniqueId } from 'lodash';

const _modal: IDataModal<ICrop> = {
  open: false,
};

const CropPage: React.FC = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ICrop[]>([]);
  const [action, setAction] = useState<'create' | 'update' | 'match'>('create');
  const [selected, setSelected] = useState<ICrop[]>([]);
  const [modal, setModal] = useState<IDataModal<ICrop>>({ ..._modal });
  const [form] = Form.useForm();
  const [tab, setTab] = useState<'create' | 'update'>('create');

  // const { data, refetch } = useQuery<IBrandRes, Error>({
  //   queryKey: ['search-brand-list'],
  //   queryFn: searchBrands,
  //   retry: 0,
  // });
  const { data, refetch } = useQuery<ICropRes, Error>({
    queryKey: ['get-crop'],
    queryFn: getCrop,
    retry: 0,
  });

  const actionCropMutation = useMutation({
    mutationFn: async (data: Partial<ICrop>) =>
      tab === 'create' ? await addCrop(data) : await updateCrop(data),
    mutationKey: tab === 'create' ? ['add-crop'] : ['update-crop'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'create') {
        notification.success({ message: 'Sync Crop สำเร็จ' });
        refetch();
      } else if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Update Crop สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Sync Crop ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  const handleSyncSelectedCrops = async (selectedRows: any) => {
    await actionCropMutation.mutateAsync(selectedRows);
  };

  const actionDeleteCrop = useMutation({
    mutationFn: async (data: Partial<ICrop>) => await deleteCrop(data),
    mutationKey: ['delete-crop'],
    onSuccess: data => {
      if (data.status === 'OK' && tab === 'update') {
        notification.success({ message: 'Delete Crop สำเร็จ' });
        refetch();
      } else {
        notification.error({ message: 'Delete Crop ไม่สำเร็จ' });
      }
    },
    onError: error => {
      console.log('error', error);
    },
  });

  // const handleSyncTOERP = async () => {
  //   await actionTOERP.mutateAsync();
  // };

  const handleDeleteCrops = async (selectedRows: any) => {
    await actionDeleteCrop.mutateAsync(selectedRows);
  };

  const handleUpdateCropCode = useCallback((item: ICrop) => {
    setModal({
      open: true,
      item: item,
    });
  }, []);

  const columns = useMemo(
    () =>
      [
        {
          title: 'ลำดับ',
          dataIndex: '',
          width: 40,
          align: 'center' as const,
          className: 'text-bold',
          render: (_: any, __: ICrop, i: number) => (
            <div className="font-weight-bold">{i + 1}</div>
          ),
        },
        {
          title: 'รหัสรอบเพาะปลูก',
          dataIndex: 'WO_LOT',
          defaultSortOrder: 'ascend',
          sorter: (a, b) =>
            Number(a.WO_LOT?.replace(/\D/g, '').replace('-', '')) -
            Number(b.WO_LOT?.replace(/\D/g, '').replace('-', '')),
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO_LOT)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO_LOT?.startsWith(value as string);
          },
        },
        {
          title: 'รุ่นเพาะปลูก',
          dataIndex: 'WO_NBR',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO_NBR)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO_NBR?.startsWith(value as string);
          },
        },
        {
          title: 'สินค้า',
          dataIndex: 'WO_PART',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO_PART)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO_PART?.startsWith(value as string);
          },
        },
        {
          title: 'ชื่อ',
          dataIndex: 'PT_DESC1',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.PT_DESC1)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.PT_DESC1?.startsWith(value as string);
          },
        },
        {
          title: 'เกษตรกร',
          dataIndex: 'WO__QADC02',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO__QADC02)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO__QADC02?.startsWith(value as string);
          },
        },
        {
          title: 'ตำบล',
          dataIndex: 'WO__CHR02',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO__CHR02)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO__CHR02?.startsWith(value as string);
          },
        },
        {
          title: 'อำเภอ',
          dataIndex: 'WO__CHR03',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO__CHR03)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO__CHR03?.startsWith(value as string);
          },
        },
        {
          title: 'จังหวัด',
          dataIndex: 'WO__CHR04',
          filterSearch: true,
          filters: Array.from(new Set(selected.map(item => item.WO__CHR04)))
            .sort(
              (a, b) =>
                Number(a?.replace(/\D/g, '').replace('-', '')) -
                Number(b?.replace(/\D/g, '').replace('-', '')),
            )
            .map(code => {
              return { text: code, value: code };
            }),
          onFilter: (value, record) => {
            return record?.WO__CHR04?.startsWith(value as string);
          },
        },
      ] as TableColumnsType<ICrop>,
    [tab, selected, handleUpdateCropCode],
  );

  const insert = data?.insert;
  const update = data?.update;
  const match = data?.match;
  useEffect(() => {
    const listMap = { create: insert, update, match };
    setSelected(listMap[tab]?.map(c => ({ ...c, _key: uniqueId('b-') })) || []);
    setSelectedRowKeys([]);
  }, [tab, insert, update, match]);

  const rowSelection = {
    onChange: (_: React.Key[], selectedRows: ICrop[]) => {
      setSelectedRowKeys(selectedRows.map(row => row._key));
      setSelectedRows(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: ICrop) => ({
      disabled: action === 'match',
    }),
  };

  const [search, SetSearch] = useState('');
  const [searchText1, setSearchText1] = useState('');
  const [searchText2, setSearchText2] = useState('');

  const filteredData = useMemo(() => {
    return selected.filter(item => {
      const cropCode = (item.WO_LOT || '').toLowerCase();
      const searchValue = search.trim().toLowerCase();
      return !searchValue || cropCode.includes(searchValue);
    });
  }, [selected, action, search, searchText1, searchText2]);

  // const onFinish: FormProps<IBrand>['onFinish'] = async values => {
  //   await matchBrandApi.mutateAsync({
  //     ...values,
  //     BRAND_SING_ID: modal.item?.BRAND_SING_ID,
  //   });
  // };

  return (
    <div>
      <Flex justify="space-between" gap={2} className="mb-3">
        <Flex gap={0}>
          <Radio.Group value={tab} onChange={e => setTab(e.target.value)}>
            <Radio.Button value="create">เพิ่ม</Radio.Button>
            <Radio.Button value="update">แก้ไข/ปรับปรุง</Radio.Button>
          </Radio.Group>
        </Flex>
        <Flex gap={12}>
          {tab === 'update' ? (
            <button
              type="button"
              className="btn btn-info"
              style={{ minWidth: 100 }}
              onClick={() => handleDeleteCrops(selectedRows)}
            >
              {actionDeleteCrop.isPending ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="sr-only">Loading...</span>
                </>
              ) : (
                'ลบข้อมูล Crop'
              )}
            </button>
          ) : (
            <></>
          )}

          <button
            type="button"
            className="btn btn-success"
            style={{ minWidth: 100 }}
            disabled={!selectedRowKeys.length}
            onClick={() => handleSyncSelectedCrops(selectedRows)}
          >
            {actionCropMutation.isPending ? (
              <>
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span className="sr-only">Loading...</span>
              </>
            ) : tab === 'create' ? (
              'ส่งข้อมูลไป OTOD >'
            ) : (
              'แก้ไขข้อมูลที่ OTOD >'
            )}
          </button>
        </Flex>
      </Flex>
      <Table<ICrop>
        rowSelection={{ type: 'checkbox', ...rowSelection }}
        columns={columns}
        dataSource={filteredData}
        size="small"
        bordered
        rowKey={r => r._key}
        pagination={false}
        locale={{ emptyText: 'ไม่พบรายการ' }}
        tableLayout="fixed"
      />
      <Suspense>
        <Modal
          open={modal.open}
          width={350}
          onCancel={() => setModal({ ..._modal })}
          // loading={matchBrandApi.isPending}
          // onOk={() => {
          //   form
          //     .validateFields()
          //     .then(values => {
          //       onFinish(values);
          //     })
          // .catch(() => {
          //       // validation failed
          //     });
          // }}
        >
          <Form
            form={form}
            name="crop"
            layout="vertical"
            // onFinish={onFinish}
            autoComplete="off"
          >
            <Form.Item<ICrop>
              label="WO_LOT CODE"
              name="WO_LOT"
              rules={[{ required: true }]}
            >
              <Select
                style={{ minWidth: 150 }}
                size="large"
                showSearch
                allowClear
                placeholder="ค้นหา"
                filterOption={(input, option) =>
                  ((option?.label as string) ?? '')
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
                options={[...(insert || [])].sort().map(frm => ({
                  label: `${frm.WO_LOT} - ${frm.WO_LOT}`,
                  value: frm.WO_LOT,
                }))}
              />
            </Form.Item>
          </Form>
        </Modal>
      </Suspense>
    </div>
  );
};

export default CropPage;
